export type AuthConfig = { cognitoDomain: string; clientId: string; redirectUri: string };

const SCOPES = ["openid", "email", "profile"];

export function buildAuthorizeUrl(cfg: AuthConfig, codeChallenge: string, state: string): string {
  const u = new URL(cfg.cognitoDomain + "/oauth2/authorize");
  u.searchParams.set("response_type", "code");
  u.searchParams.set("client_id", cfg.clientId);
  u.searchParams.set("redirect_uri", cfg.redirectUri);
  u.searchParams.set("scope", SCOPES.join(" "));
  u.searchParams.set("code_challenge_method", "S256");
  u.searchParams.set("code_challenge", codeChallenge);
  u.searchParams.set("state", state);
  return u.toString();
}

export function parseCallback(search: string): { code: string; state: string | null } | null {
  const p = new URLSearchParams(search);
  const err = p.get("error");
  if (err) throw new Error(`${err}: ${p.get("error_description") ?? ""}`);
  const code = p.get("code");
  return code ? { code, state: p.get("state") } : null;
}

function b64url(b: Uint8Array): string {
  return btoa(String.fromCharCode(...b)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** Browser-only: needs window.crypto.subtle, which requires a secure context. */
export async function pkceChallenge(): Promise<{ verifier: string; challenge: string }> {
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  const verifier = b64url(bytes);
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return { verifier, challenge: b64url(new Uint8Array(digest)) };
}

export async function exchangeCode(cfg: AuthConfig, code: string, verifier: string): Promise<string> {
  const res = await fetch(cfg.cognitoDomain + "/oauth2/token", {
    method: "POST",
    headers: { "content-type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      grant_type: "authorization_code",
      client_id: cfg.clientId,
      code,
      redirect_uri: cfg.redirectUri,
      code_verifier: verifier,
    }),
  });
  if (!res.ok) throw new Error(`token exchange failed: ${res.status}`);
  const body = (await res.json()) as { id_token?: string };
  if (!body.id_token) throw new Error("token response carried no id_token");
  return body.id_token;
}
